import { useEffect, useState } from "react";
import { FounderOperationalShell } from "../../../components/founder/FounderOperationalShell";
import { FounderConfirmPublish } from "../../../components/founder/FounderConfirmPublish";
import { PasswordInputWithToggle } from "../../../components/PasswordInputWithToggle";
import { useFounderAuth } from "../../../context/FounderAuthContext";
import {
  activateFounderMfa,
  beginFounderMfaSetup,
  changeFounderPassword,
  fetchFounderMfaStatus,
} from "../../../founder/founderWorkspaceApi";

export function FounderAccountWorkspace() {
  const { user, signOutOtherSessions, fetchSecuritySummary } = useFounderAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmChange, setConfirmChange] = useState(false);
  const [confirmSignOutOthers, setConfirmSignOutOthers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [activeSessions, setActiveSessions] = useState(1);
  const [mfaStatus, setMfaStatus] = useState("Off");
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaSetupQr, setMfaSetupQr] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const metrics = [
    { id: "role", label: "Role", value: "Founder", tone: "steel" as const },
    { id: "status", label: "Session", value: "Active", tone: "mint" as const },
  ];

  useEffect(() => {
    Promise.all([fetchSecuritySummary(), fetchFounderMfaStatus()])
      .then(([summary, mfa]) => {
        setActiveSessions(summary.activeSessions);
        setMfaStatus(mfa.statusLabel);
      })
      .catch(() => {
        // Keep defaults if summary is temporarily unavailable.
      });
  }, [fetchSecuritySummary]);

  async function submitPasswordChange() {
    if (newPassword !== confirmPassword) {
      setMessage("New passwords do not match.");
      return;
    }

    setSubmitting(true);
    setMessage("");

    try {
      await changeFounderPassword({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setConfirmChange(false);
      setMessage("Password updated.");
      const summary = await fetchSecuritySummary();
      setActiveSessions(summary.activeSessions);
    } catch {
      setMessage("Could not update password. Check your current password.");
    } finally {
      setSubmitting(false);
    }
  }

  async function submitSignOutOthers() {
    setSubmitting(true);
    setMessage("");
    try {
      await signOutOtherSessions();
      setConfirmSignOutOthers(false);
      const summary = await fetchSecuritySummary();
      setActiveSessions(summary.activeSessions);
      setMessage("Other sessions signed out.");
    } catch {
      setMessage("Could not sign out other sessions.");
    } finally {
      setSubmitting(false);
    }
  }

  async function startMfaSetup() {
    setSubmitting(true);
    setMessage("");
    setRecoveryCodes(null);
    try {
      const setup = await beginFounderMfaSetup();
      setMfaSetupQr(setup.qrCodeDataUrl);
      setMfaCode("");
      setShowMfaSetup(true);
    } catch {
      setMessage("Could not start two-step verification setup.");
      setShowMfaSetup(false);
    } finally {
      setSubmitting(false);
    }
  }

  async function finishMfaSetup() {
    setSubmitting(true);
    setMessage("");
    try {
      const result = await activateFounderMfa(mfaCode.trim());
      setRecoveryCodes(result.recoveryCodes);
      setMfaSetupQr(null);
      setMfaCode("");
      setMfaStatus("On");
      setMessage("Two-step verification is on. Save your recovery codes somewhere safe.");
    } catch {
      setMessage("Could not enable two-step verification. Check the code and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function cancelMfaSetup() {
    setShowMfaSetup(false);
    setMfaSetupQr(null);
    setMfaCode("");
  }

  return (
    <FounderOperationalShell metrics={metrics} title="My Account">
      <div className="oac-founder-ops-grid oac-founder-ops-grid--2col">
        <section className="oac-founder-ops-panel">
          <h2 className="oac-founder-ops-panel__title">Profile</h2>
          <ul className="oac-founder-ops-list">
            <li className="oac-founder-ops-list__item">
              <div>
                <span className="oac-founder-ops-list__title">Name</span>
                <span className="oac-founder-ops-list__meta">{user?.fullName ?? "—"}</span>
              </div>
            </li>
            <li className="oac-founder-ops-list__item">
              <div>
                <span className="oac-founder-ops-list__title">Email</span>
                <span className="oac-founder-ops-list__meta">{user?.email ?? "—"}</span>
              </div>
            </li>
          </ul>
        </section>

        <section className="oac-founder-ops-panel">
          <h2 className="oac-founder-ops-panel__title">Login & security</h2>
          <ul className="oac-founder-ops-list">
            <li className="oac-founder-ops-list__item">
              <div>
                <span className="oac-founder-ops-list__title">Session</span>
                <span className="oac-founder-ops-list__meta">
                  Signed in to Founder Control Centre
                  {activeSessions > 1 ? ` · ${activeSessions} active sessions` : ""}
                </span>
              </div>
              <span className="oac-founder-badge oac-founder-badge--healthy">Active</span>
            </li>
            <li className="oac-founder-ops-list__item">
              <div>
                <span className="oac-founder-ops-list__title">Two-step verification</span>
                <span className="oac-founder-ops-list__meta">Optional · currently Off</span>
              </div>
              <span
                className={`oac-founder-badge oac-founder-badge--${
                  mfaStatus === "On" ? "healthy" : "neutral"
                }`}
              >
                {mfaStatus}
              </span>
            </li>
          </ul>

          <div className="oac-founder-field oac-founder-account-security__password-start">
            <label htmlFor="founder-current-password">Current password</label>
            <PasswordInputWithToggle
              autoComplete="current-password"
              id="founder-current-password"
              name="currentPassword"
              onChange={(e) => setCurrentPassword(e.target.value)}
              value={currentPassword}
            />
          </div>
          <div className="oac-founder-field">
            <label htmlFor="founder-new-password">New password</label>
            <PasswordInputWithToggle
              autoComplete="new-password"
              id="founder-new-password"
              name="newPassword"
              onChange={(e) => setNewPassword(e.target.value)}
              value={newPassword}
            />
          </div>
          <div className="oac-founder-field">
            <label htmlFor="founder-confirm-password">Confirm new password</label>
            <PasswordInputWithToggle
              autoComplete="new-password"
              id="founder-confirm-password"
              name="confirmPassword"
              onChange={(e) => setConfirmPassword(e.target.value)}
              value={confirmPassword}
            />
          </div>

          <div className="oac-founder-account-security__actions">
            {!confirmChange ? (
              <button
                className="oac-founder-btn oac-founder-btn--primary"
                disabled={!currentPassword || !newPassword || !confirmPassword}
                onClick={() => setConfirmChange(true)}
                type="button"
              >
                Change password
              </button>
            ) : (
              <FounderConfirmPublish
                message="Confirm password change?"
                onCancel={() => setConfirmChange(false)}
                onConfirm={submitPasswordChange}
                submitting={submitting}
              />
            )}

            {!confirmSignOutOthers ? (
              <button
                className="oac-founder-btn oac-founder-btn--secondary"
                onClick={() => setConfirmSignOutOthers(true)}
                type="button"
              >
                Sign out other sessions
              </button>
            ) : (
              <FounderConfirmPublish
                message="Sign out all other sessions? This device stays signed in."
                onCancel={() => setConfirmSignOutOthers(false)}
                onConfirm={submitSignOutOthers}
                submitting={submitting}
              />
            )}

            {mfaStatus === "Off" && !showMfaSetup && !recoveryCodes ? (
              <button
                className="oac-founder-btn oac-founder-btn--secondary"
                disabled={submitting}
                onClick={startMfaSetup}
                type="button"
              >
                Enable two-step verification
              </button>
            ) : null}
          </div>

          {showMfaSetup && mfaSetupQr ? (
            <div style={{ marginTop: "0.85rem" }}>
              <p className="oac-founder-ops-footnote">
                Scan this code with your authenticator app, then enter the 6-digit code.
              </p>
              <img alt="Authenticator QR code" height={160} src={mfaSetupQr} width={160} />
              <div className="oac-founder-field">
                <label htmlFor="founder-mfa-activate-code">Verification code</label>
                <input
                  id="founder-mfa-activate-code"
                  inputMode="numeric"
                  onChange={(e) => setMfaCode(e.target.value)}
                  value={mfaCode}
                />
              </div>
              <div className="oac-founder-actions">
                <button
                  className="oac-founder-btn oac-founder-btn--primary"
                  disabled={submitting || mfaCode.trim().length < 6}
                  onClick={finishMfaSetup}
                  type="button"
                >
                  Confirm and turn on
                </button>
                <button
                  className="oac-founder-btn oac-founder-btn--secondary"
                  disabled={submitting}
                  onClick={cancelMfaSetup}
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}

          {recoveryCodes ? (
            <div style={{ marginTop: "0.85rem" }}>
              <p className="oac-founder-ops-footnote">
                Save these recovery codes somewhere safe. Each can be used once if you lose your
                authenticator app.
              </p>
              <ul className="oac-founder-ops-list oac-founder-ops-list--compact">
                {recoveryCodes.map((code) => (
                  <li className="oac-founder-ops-list__item" key={code}>
                    <span className="oac-founder-ops-list__title">{code}</span>
                  </li>
                ))}
              </ul>
              <button
                className="oac-founder-btn oac-founder-btn--primary"
                onClick={() => setRecoveryCodes(null)}
                type="button"
              >
                I have saved these codes
              </button>
            </div>
          ) : null}

          {message ? <p className="oac-founder-ops-detail__result">{message}</p> : null}
        </section>
      </div>
    </FounderOperationalShell>
  );
}
